<script>
import MdmUploadSection from './MdmUploadSection.vue';
import MdmBatchApprovalList from './MdmBatchApprovalList.vue';
import {
    getCommandLogs,
    approveImportBatch,
    rejectImportBatch,
    updateCommandLogsStatus,
    getImportBatches,
} from '@/helpers/api/mdm/mdm-command-logs.api';
import { commonService } from '@/helpers/common.service';

// Map numeric status → display label (V2 Enum)
const STATUS_LABELS = {
    0: 'Uploaded',
    1: 'Queue',
    2: 'Processing',
    3: 'Waiting Approval',
    4: 'Approved',
    5: 'Rejected',
    6: 'Failed',
};

// Map tab name → numeric type (1=Send Warning, 2=Enable Lost Mode, 3=Disable Lost Mode)
const TAB_TYPE = { warning: 1, locked: 2, unlocked: 3 };

export default {
    name: 'MdmApprovalTable',
    components: {
        MdmUploadSection,
        MdmBatchApprovalList,
    },

    props: {
        currentTab:  { type: String, default: 'warning' },
        isRequester: { type: Boolean, default: false },
        isApprover:  { type: Boolean, default: false },
        autoOpenBatchId: { type: [String, Number], default: null },
    },

    data() {
        return {
            activeTab:   'table',
            actionLoading: false,
            
            // Modal state
            selectedBatch: null,
            selectedBatchRecords: [],
            loadingBatchRecords: false,
            batchStats: {
                total: 0,
                success: 0,
                waiting: 0,
                fail: 0
            },
            
            // Partial Selection
            selectedRecordIds: [],
            partialLoading: false
        };
    },

    computed: {
        /** Numeric process type for the active tab */
        processType() { return TAB_TYPE[this.currentTab] ?? 1; },

        processInfo() {
            const map = {
                1: { label: 'Send Warning', color: 'badge-soft-warning' },
                2: { label: 'Enable Lost Mode',    color: 'badge-soft-danger' },
                3: { label: 'Disable Lost Mode',  color: 'badge-soft-success' },
            };
            return map[this.processType] ?? { label: 'Unknown', color: 'badge-soft-secondary' };
        }
    },

    methods: {
        // ─── Navigation ──────────────────────────────────────────────────────
        setActiveTab(tab) {
            this.activeTab = tab;
            if (tab === 'table') {
                this.fetchCommandLogs();
            }
        },

        // ─── Data Fetching ────────────────────────────────────────────────────
        async openBatchDetails(batch) {
            this.selectedBatch = batch;
            this.loadingBatchRecords = true;
            this.selectedBatchRecords = [];
            this.selectedRecordIds = []; // Reset selection
            this.batchStats = { total: 0, success: 0, waiting: 0, fail: 0 };

            try {
                // 1. Refresh batch summary header info (Status, Processed Date, etc.)
                try {
                    const batchRes = await getImportBatches({ batch_id: batch.id, type: this.processType });
                    const updatedBatch = (batchRes.data?.data?.[0]) || (batchRes.data?.[0]) || (batchRes?.[0]);
                    if (updatedBatch) {
                        this.selectedBatch = updatedBatch;
                    }
                } catch (err) {
                    console.error("Refresh batch summary failed:", err);
                }

                // 2. Fetch Individual Records
                const params = {
                    type:     this.processType,
                    batch_id: this.selectedBatch.id,
                    paginate: 0,
                };
                const response = await getCommandLogs(params);
                const records = response.data ?? [];
                
                this.selectedBatchRecords = records;
                this.batchStats.total = records.length;
                this.batchStats.success = records.filter(r => r.status === 4).length;
                this.batchStats.waiting = records.filter(r => [0, 1, 2, 3].includes(r.status)).length;
                this.batchStats.fail = records.filter(r => [5, 6].includes(r.status)).length;

            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Failed to load details');
            } finally {
                this.loadingBatchRecords = false;
            }
        },

        fetchCommandLogs() {
            if (this.$refs.batchList) {
                this.$refs.batchList.fetchBatches();
            }
        },

        async handleBatchAction(action) {
            if (!this.selectedBatch || !this.selectedBatch.id) return;

            const label = action === 'approve' ? 'Approve' : 'Reject';
            const confirmed = await commonService.swalConfirm(
                `${label} entire batch?`, 
                `This will ${label} the entire batch (#${this.selectedBatch.id}) and all its associated records.`
            );
            if (!confirmed) return;

            this.actionLoading = true;
            try {
                let response;
                if (action === 'approve') {
                    response = await approveImportBatch(this.selectedBatch.id);
                } else {
                    response = await rejectImportBatch(this.selectedBatch.id);
                }
                
                commonService.swalPrompt(true, response.message || `Batch #${this.selectedBatch.id} has been ${action}d.`, "Success");

                // Check if batch is completed
                if (response.is_completed) {
                   const closeBtn = document.querySelector('#mdmBatchRecordsModal .btn-close');
                   if (closeBtn) closeBtn.click();
                } else {
                    this.openBatchDetails(this.selectedBatch);
                }

                // Refresh list
                this.fetchCommandLogs();

            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, `Action Failed`);
            } finally {
                this.actionLoading = false;
            }
        },

        async handlePartialAction(action, specificIds = null) {
            const ids = specificIds || this.selectedRecordIds;
            if (!ids || ids.length === 0) return;

            const label = action === 'approve' ? 'Approve' : 'Reject';
            if (!specificIds) {
                const confirmed = await commonService.swalConfirm(
                    `${label} selected records?`,
                    `Are you sure you want to ${label} the ${ids.length} selected records?`
                );
                if (!confirmed) return;
            }

            this.partialLoading = true;
            try {
                const response = await updateCommandLogsStatus(ids, action);
                const data = response.data || {};
                
                commonService.swalPrompt(true, data.message || `${ids.length} records ${action}d successfully.`, 'Success');
                
                // Clear selection
                this.selectedRecordIds = [];

                // Refresh data
                await this.openBatchDetails(this.selectedBatch);
                this.fetchCommandLogs();

            } catch (error) {
                const msg = error.response?.data?.message ?? error.message;
                commonService.swalPrompt(false, msg, 'Action Failed');
            } finally {
                this.partialLoading = false;
            }
        },

        toggleAllRecords() {
            const waiterIds = this.selectedBatchRecords
                .filter(r => [1, 3].includes(r.status))
                .map(r => r.id);
            
            if (this.selectedRecordIds.length === waiterIds.length) {
                this.selectedRecordIds = [];
            } else {
                this.selectedRecordIds = waiterIds;
            }
        },

        statusLabel(status) {
            return STATUS_LABELS[status] ?? `Status ${status}`;
        },

        statusClass(status) {
            const map = {
                0: 'badge-soft-secondary', // Uploaded
                1: 'badge-soft-info',      // Queue
                2: 'badge-soft-primary',   // Processing
                3: 'badge-soft-warning',   // Waiting Approval
                4: 'badge-soft-success',   // Approved
                5: 'badge-soft-dark',      // Rejected
                6: 'badge-soft-danger',    // Failed
            };
            return map[status] ?? 'badge-soft-secondary';
        },

        async initAutoOpen() {
            if (!this.autoOpenBatchId) return;
            
            this.loadingBatchRecords = true;
            try {
                // 1. Fetch the specific batch object
                const params = { 
                    batch_id: this.autoOpenBatchId,
                    type: this.processType 
                };
                const res = await getImportBatches(params);
                const batch = (res.data?.data?.[0]) || (res.data?.[0]) || (res?.[0]);

                if (!batch) {
                    console.warn(`Batch #${this.autoOpenBatchId} not found for this type.`);
                    return;
                }

                // 2. Load details (records)
                await this.openBatchDetails(batch);

                // 3. Trigger Modal via hidden button click (most reliable way)
                await this.$nextTick();
                setTimeout(() => {
                    const btn = this.$refs.autoOpenBtn;
                    if (btn) {
                        btn.click();
                    } else {
                        console.error("Auto-open button ref not found");
                        // Fallback to window.bootstrap if available
                        const modalEl = document.getElementById('mdmBatchRecordsModal');
                        if (window.bootstrap && window.bootstrap.Modal) {
                            new window.bootstrap.Modal(modalEl).show();
                        }
                    }
                }, 200);

            } catch (error) {
                console.error("Auto-open error:", error);
            } finally {
                this.loadingBatchRecords = false;
            }
        },
    },

    async mounted() {
        if (this.autoOpenBatchId) {
            this.initAutoOpen();
        }
    },

    watch: {
        // Handle cases where the ID changes while on the same page
        autoOpenBatchId: {
            handler(newVal) {
                if (newVal) this.initAutoOpen();
            }
        }
    },
};
require('@/assets/css/datatable.css');
</script>

<template>
    <div class="card shadow-none border-0 mb-0">
        <!-- Hidden button for auto-opening modal -->
        <button ref="autoOpenBtn" data-bs-toggle="modal" data-bs-target="#mdmBatchRecordsModal" class="d-none"></button>

        <!-- Sub-tab navigation: Table | Upload Devices -->
        <div class="card-header p-0 bg-light-subtle border-bottom">
            <ul class="nav nav-tabs-custom nav-success border-bottom-0" role="tablist">
                <li class="nav-item">
                    <a
                        class="nav-link py-3"
                        :class="{ active: activeTab === 'table' }"
                        @click.prevent="setActiveTab('table')"
                        href="#"
                        role="tab"
                    >
                        <i class="ri-table-line me-1 align-bottom"></i> Main Table
                    </a>
                </li>
                <li v-if="isRequester && ['warning', 'locked', 'unlocked'].includes(currentTab)" class="nav-item">
                    <a
                        class="nav-link py-3"
                        :class="{ active: activeTab === 'upload' }"
                        @click.prevent="setActiveTab('upload')"
                        href="#"
                        role="tab"
                    >
                        <i class="ri-upload-cloud-2-line me-1 align-bottom"></i> Request Approval
                    </a>
                </li>
            </ul>
        </div>

        <div class="tab-content">
            <!-- ── Main Table Tab (Batch List only) ────────────────────────── -->
            <div v-if="activeTab === 'table'" class="tab-pane active animated fadeIn">
                <div class="p-4">
                    <MdmBatchApprovalList 
                        ref="batchList"
                        :process-type="processType" 
                        :is-approver="isApprover"
                        @view-records="openBatchDetails"
                        @action-performed="fetchCommandLogs"
                    />
                </div>
            </div>

            <!-- ── Upload Tab ─────────────────────────────────────────────── -->
            <div v-if="activeTab === 'upload'" class="tab-pane active p-4 animated fadeIn">
                <MdmUploadSection
                    :is-requester="isRequester"
                    :process-type="processType"
                    @uploaded="fetchCommandLogs()"
                    @batch-created="setActiveTab('table')"
                    @view-history="setActiveTab('table')"
                />
            </div>
        </div>
    </div>

    <!-- ── Batch Record Details Modal ────────────────────────────────────── -->
    <div class="modal fade" id="mdmBatchRecordsModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-xl modal-dialog-centered">
            <div class="modal-content border-0">
                <div class="modal-header bg-soft-info p-3">
                    <h5 class="modal-title text-info d-flex align-items-center" id="exampleModalLabel">
                        MDM Batch Submission Review
                        <span :class="['badge ms-2 text-uppercase border', processInfo.color]" style="font-size: 10px;">{{ processInfo.label }}</span>
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-4">
                    <div v-if="loadingBatchRecords" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div v-else-if="selectedBatch">
                        <!-- Batch Header Info (Compact 2x3 Grid) -->
                        <div class="row mb-4 border-bottom pb-3 g-3">
                            <!-- Row 1 -->
                            <div class="col-md-4">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Batch ID</span>
                                <h6 class="mb-0 text-primary fw-bold">#{{ selectedBatch.id }}</h6>
                            </div>
                            <div class="col-md-4">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Requester</span>
                                <h6 class="mb-0 text-dark fw-bold text-truncate">{{ selectedBatch.importer?.name || '---' }}</h6>
                            </div>
                            <div class="col-md-4">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Total Records</span>
                                <h6 class="mb-0 text-dark fw-bold">{{ selectedBatchRecords.length }} Rows</h6>
                            </div>

                            <!-- Row 2 -->
                            <div class="col-md-4">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Status</span><br>
                                <span :class="['badge', statusClass(selectedBatch.status), 'fs-11 px-3 py-1']">
                                    {{ selectedBatch.status_label || statusLabel(selectedBatch.status) }}
                                </span>
                            </div>
                            <div class="col-md-4">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Processed Date</span>
                                <h6 class="mb-0 text-dark fw-bold">{{ selectedBatch.completed_at || '---' }}</h6>
                            </div>
                            <div class="col-md-4">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Process Type</span>
                                <h6 class="mb-0 text-dark fw-bold">{{ processInfo.label }} Process</h6>
                            </div>
                            <div class="col-md-8">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Source File</span>
                                <div class="d-flex align-items-center gap-2">
                                    <p class="mb-0 text-muted fs-11 text-break line-height-1">{{ selectedBatch.filename || '---' }}</p>
                                    <a v-if="selectedBatch.download_link" :href="selectedBatch.download_link" target="_blank" class="btn btn-soft-primary btn-sm btn-icon waves-effect waves-light shadow-none" title="Download Source File">
                                        <i class="ri-download-2-line"></i>
                                    </a>
                                </div>
                            </div>

                            <!-- Template Info (Full Width) -->
                            <div class="col-md-12" v-if="selectedBatchRecords.length > 0 && selectedBatchRecords[0].template">
                                <span class="text-muted fs-11 text-uppercase fw-bold">Message Template</span>
                                <div class="p-2 bg-light rounded border fs-12 mt-1">
                                    <h6 class="fs-13 fw-bold mb-1 text-dark">{{ selectedBatchRecords[0].template.name }}</h6>
                                    <p class="mb-1 text-muted font-italic line-height-1 small">"{{ selectedBatchRecords[0].template.message_body }}"</p>
                                    <div v-if="selectedBatchRecords[0].template.footnote" class="mt-2 pt-2 border-top italic text-muted small">
                                        {{ selectedBatchRecords[0].template.footnote }}
                                    </div>
                                    <div v-if="selectedBatchRecords[0].template.phone_number" class="mt-1 fw-medium text-dark small">
                                        <i class="ri-phone-line me-1"></i> {{ selectedBatchRecords[0].template.phone_number }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Instruction Note for Approvers -->
                        <div v-if="isApprover && [1, 3].includes(selectedBatch.status)" class="alert alert-info border-info border-opacity-25 d-flex align-items-center p-2 mb-3 shadow-none">
                            <i class="ri-information-line fs-16 me-2 text-info"></i>
                            <div class="fs-12 text-info">
                                <strong>Approver Note:</strong> Please select the individual records you wish to process using the checkboxes in the table below to enable approval/rejection actions.
                            </div>
                        </div>

                        <!-- Bulk Action Toolbar -->
                        <div v-if="selectedRecordIds.length > 0 && isApprover" class="alert alert-soft-primary border-dashed d-flex align-items-center mb-3 p-2 animated fadeIn">
                            <div class="flex-grow-1">
                                <h6 class="alert-heading fw-bold mb-0 fs-12">
                                    <i class="ri-checkbox-multiple-line me-1 align-bottom"></i> 
                                    {{ selectedRecordIds.length }} records selected
                                </h6>
                            </div>
                            <div class="flex-shrink-0 d-flex gap-2">
                                <button class="btn btn-danger btn-sm px-3 py-1 fs-11" @click="handlePartialAction('reject')" :disabled="partialLoading">
                                    Reject Selected
                                </button>
                                <button class="btn btn-primary btn-sm px-3 py-1 fs-11" @click="handlePartialAction('approve')" :disabled="partialLoading">
                                    Approve Selected
                                </button>
                                <button class="btn btn-light btn-sm px-3 py-1 fs-11" @click="selectedRecordIds = []">
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <!-- Records Listing -->
                        <div class="d-flex align-items-center mb-3">
                            <h6 class="fs-13 fw-bold mb-0 flex-grow-1">Individual Customer Records</h6>
                            <button 
                                class="btn btn-soft-secondary btn-sm btn-icon waves-effect waves-light shadow-none" 
                                @click="openBatchDetails(selectedBatch)"
                                title="Refresh Records"
                                :disabled="loadingBatchRecords"
                            >
                                <i :class="['ri-refresh-line', { 'spinner-border spinner-border-sm border-0': loadingBatchRecords }]"></i>
                            </button>
                        </div>
                        <div class="table-responsive border rounded" style="max-height: 400px;">
                            <table class="table table-striped table-nowrap align-middle mb-0 table-sm">
                                <thead class="table-light sticky-top">
                                    <tr>
                                        <th class="ps-3" style="width: 40px;" v-if="isApprover">
                                            <input type="checkbox" class="form-check-input" @change="toggleAllRecords" :checked="selectedRecordIds.length > 0 && selectedRecordIds.length === selectedBatchRecords.filter(r => [1, 3].includes(r.status)).length">
                                        </th>
                                        <th :class="{ 'ps-3': !isApprover }">Customer Name</th>
                                        <th>Device info</th>
                                        <th>Contract & Order No</th>
                                        <th>Outstanding / Overdue</th>
                                        <th>Unpaid Invoices</th>
                                        <th>Status / Approval</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in selectedBatchRecords" :key="item.id" :class="{ 'table-active': selectedRecordIds.includes(item.id) }">
                                        <td class="ps-3" v-if="isApprover">
                                            <input type="checkbox" class="form-check-input" v-model="selectedRecordIds" :value="item.id" :disabled="![1, 3].includes(item.status) || partialLoading">
                                        </td>
                                        <td :class="{ 'ps-3': !isApprover }">
                                            <div class="fw-medium" v-if="item.customer">
                                                <router-link target="_blank" :to="`/customer/customer-details/${item.customer.id}`" class="fw-medium link-secondary">{{ item.customer.name }}</router-link>
                                            </div>
                                            <div class="fw-medium text-muted" v-else>---</div>
                                        </td>
                                        <td>
                                            <div class="fs-11 text-dark fw-bold mb-1">{{ item.device_details?.product_name ?? '---' }}</div>
                                            <div class="fs-10 text-muted d-flex flex-column gap-0">
                                                <span><span class="fw-medium">SN:</span> {{ item.device_details?.serial_number ?? '---' }}</span>
                                                <span><span class="fw-medium">IMEI 1:</span> {{ item.device_details?.imei ?? '---' }}</span>
                                                <span v-if="item.device_details?.imei2"><span class="fw-medium">IMEI 2:</span> {{ item.device_details?.imei2 }}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="mb-1" v-if="item.contract">
                                                <router-link target="_blank" :to="`/contracts/customer-contract-details/${item.contract.id}`" class="fw-medium link-secondary fs-12">
                                                    {{ item.contract.number }}
                                                </router-link>
                                            </div>
                                            <div v-else class="text-muted fs-12 mb-1">---</div>
                                            <div v-if="item.order">
                                                <router-link target="_blank" :to="`/orders/customer-order-details/${item.order.id}`" class="fw-medium link-secondary fs-12">
                                                    {{ item.order.number }}
                                                </router-link>
                                            </div>
                                        </td>
                                        <td>
                                            <div v-if="item.ar_details" class="d-flex flex-column lh-1">
                                                <span class="fs-11 fw-bold text-danger">RM {{ item.ar_details.total_outstanding }}</span>
                                                <span class="fs-10 text-muted mt-1" v-if="item.ar_details.total_overdue">Overdue: RM {{ item.ar_details.total_overdue }}</span>
                                            </div>
                                            <span v-else class="text-muted fs-12">---</span>
                                        </td>
                                        <td>
                                            <span v-if="item.ar_details" class="fs-11 text-dark">{{ item.ar_details.unpaid_invoice_count }}</span>
                                            <span v-else class="text-muted fs-12">---</span>
                                        </td>
                                        <td>
                                            <div class="d-flex flex-column align-items-start">
                                                <span :class="['badge', statusClass(item.status), 'fs-10 mb-1 shadow-none']">
                                                    {{ item.status_description || statusLabel(item.status) }}
                                                </span>
                                                <!-- Approver/Rejection Info (Compact) -->
                                                <div v-if="item.submission_approval" class="lh-1">
                                                    <span class="text-dark fs-11 fw-medium" v-if="item.submission_approval.approved_by || item.submission_approval.rejected_by">
                                                        By: {{ item.submission_approval.approved_by || item.submission_approval.rejected_by }}
                                                    </span>
                                                    <div class="text-muted fs-11 mt-1" v-if="item.submission_approval.approved_at || item.submission_approval.rejected_at">
                                                        {{ item.submission_approval.approved_at || item.submission_approval.rejected_at }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-light shadow-none" data-bs-dismiss="modal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.animated { animation-duration: 0.3s; }
.fadeIn { animation-name: fadeIn; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.table thead th { font-weight: 600; text-transform: uppercase; font-size: 11px; padding: 12px 10px; }
.line-height-1 { line-height: 1.2; }
.font-italic { font-style: italic; }
.sticky-top { top: -1px; z-index: 1020; box-shadow: inset 0 -1px 0 #e9ebec; }
</style>
